import { ConnectedDropzone } from 'components/form/dropzone';
import { useFormikContext } from 'formik';
import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useResumeUpload } from '../hooks';
import { ApplicationFormState } from '../types';

interface ResumeUploaderProps {
  showOnlyRequired?: boolean;
  name: string;
}

const useStyles = makeStyles((theme) => ({
  dropzoneWrapper: {
    marginTop: theme.spacing(2),
  },
  uploadLabel: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  loader: {
    marginLeft: theme.spacing(1),
    width: '20px !important',
    height: '20px !important',
  },
  uploadedFile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  errorAlert: {
    marginTop: theme.spacing(1),
  },
}));

const ResumeUploader: React.FC<ResumeUploaderProps> = ({
  showOnlyRequired,
  name,
}) => {
  const classes = useStyles();
  const { handleResumeUpload, handleResetUpload, loading, newCvUpload } =
    useResumeUpload();
  const { errors } = useFormikContext<ApplicationFormState>();
  const resumeError = errors.resume;
  const hasError = Boolean(resumeError);

  return (
    <Box className={classes.dropzoneWrapper}>
      <Typography variant="body2" className={classes.uploadLabel}>
        Upload another Resume / CV
        {loading && <CircularProgress size={20} className={classes.loader} />}
      </Typography>
      <ConnectedDropzone
        name={name}
        withoutList
        onChange={handleResumeUpload}
        dropzoneOptions={{
          accept: '.pdf,.doc,.docx',
          maxFiles: 1,
          disabled: loading,
        }}
      />

      {newCvUpload?.hash && (
        <Box className={classes.uploadedFile}>
          <Typography variant="body2">{newCvUpload.title}</Typography>
          <IconButton
            size="small"
            onClick={handleResetUpload}
            className={classes.closeButton}
            aria-label="Remove uploaded file"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {hasError && (
        <Alert severity="error" className={classes.errorAlert}>
          {typeof resumeError === 'string' ? resumeError : 'Resume is required'}
        </Alert>
      )}
    </Box>
  );
};

export default ResumeUploader;
